import { Platform } from '@angular/cdk/platform';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { formatNow, formatTimeStamps } from '../../utils/utils';

@Component({
  selector: 'hivegpt-chat-drawer',
  templateUrl: './chat-drawer.component.html',
  styleUrls: ['./chat-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatDrawerComponent implements OnInit {
  @ViewChild('chatMain') private chatMain: ElementRef;

  @ViewChild('drawer') drawer!: MatDrawer;
  private bodyOverflowClass = 'body-overflow-hidden';
  @ViewChild('myTextarea', { static: false })
  myTextarea!: ElementRef<HTMLTextAreaElement>; // Reference to the textarea

  @Input() apiKey!: string;
  @Input() bgBubbleAi!: string;
  @Input() bgBubbleUser!: string;
  @Input() bgGradient!: string[];
  @Input() botName!: string;
  @Input() botId!: string;
  @Input() closeButtonColor!: string;
  @Input() closeButtonbgColor!: string;
  @Input() credentials!: [];
  @Input() dateTimeColor!: string;
  @Input() dateTextColor!: string;
  @Input() eventId!: string;
  @Input() eventName!: string;
  @Input() formFieldBgColor!: string;
  @Input() formFieldTextColor!: string;
  @Input() fullView!: boolean;
  @Input() gradientColors!: string[];
  @Input() greeting!: string;
  @Input() messageTextColorAi!: string;
  @Input() messageTextColorUser!: string;
  @Input() rules!: string;
  @Input() sendButtonColor!: string;
  @Input() sendButtonTextColor!: string;
  @Input() showClose!: boolean;
  @Input() thumbsDownMessages!: string[];
  @Input() thumbsUpMessage!: string;
  @Input() timezone!: string;
  @Input() unknownResponses!: [];
  @Input() useOpenAi!: boolean;
  @Input() userId!: string;

  @Input() againButtonColor!: string[];
  @Input() againButtonTextColor!: string[];
  @Output() feedbackEvent = new EventEmitter<string>();
  @Output() onCloseEvent = new EventEmitter<void>();

  aiResponse: string = '';
  chatLog: any[] = [];
  decoder = new TextDecoder();
  feedbackDone: boolean = false;
  greetingMsg: string = '';
  hasBackdropValue: boolean = false;
  input: string = '';
  listenerAdded = false;
  loading: boolean = false;
  mode: string = 'over';
  quickPrompts: any[] = [];
  thumbsDownMsgIndex = 0;
  userName: string = '';
  showStartAgain: boolean = false;
  isIOSDevice: boolean = false;
  showFeedBackIconsIndex: number | null = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private platform: Platform
  ) {
    this.chatMain = new ElementRef(null);
    if (this.platform.IOS) {
      this.isIOSDevice = true;
    }
  }

  ngOnInit(): void {
    this.fetchBotConfig().subscribe(
      (res) => {
        this.cdr.markForCheck();
        this.fetchChatHistory().subscribe(
          (response) => {
            this.mapChatHistory(response);
            this.cdr.markForCheck();
          },
          (err) => {
            console.error('Error fetching chat history:', err);
          }
        );
      },
      (err) => {
        console.error('Error fetching chat history:', err);
      }
    );
    this.cdr.markForCheck();
  }
  onStartAgain() {
    if (this.loading) return;
    this.showStartAgain = false;
    this.chatLog = [];
    this.chatLog.push({
      type: 'ai',
      message:
        this.greetingMsg ||
        this.processMessage(
          "Greetings! I'm your COP28 virtual assistant. Have questions about the event? I'm here to help."
        ),
      time: formatNow(this.timezone),
    });
    this.archieveMessages().subscribe();
  }

  archieveMessages() {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': this.apiKey,
    });
    const url = `${environment.BASE_URL}/bot/clear-history/${this.botId}/${this.userId}`;
    return this.http.post(url, {}, { headers }).pipe(
      switchMap((res: any) => {
        return of(res);
      }),
      catchError((error) => {
        return of(null);
      })
    );
  }

  fetchBotConfig() {
    const url = `${environment.BASE_URL}/bot/config?bot_id=${this.botId}`;
    return this.http.get(url).pipe(
      switchMap((res: any) => {
        this.botName = res.name;
        this.greetingMsg = res.greeting;

        this.thumbsDownMessages = res?.negativeResponses?.length
          ? res.negativeResponses
          : [
              "We are sorry we've not been able to answer your question.<br/> However, our dedicated support team is happy to help. <span class='feedback-link' style='cursor: pointer; text-decoration: underline; font-weight: 700; color: #17235B;'>Please click here</span> and a human agent will assist you as soon as possible",
            ];
        this.quickPrompts = res.quickPrompts;
        this.cdr.markForCheck();
        return of(res);
      }),
      catchError((error) => {
        console.error('Error fetching chatbot config: ', error);
        return of(null);
      })
    );
  }

  fetchChatHistory() {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': this.apiKey,
    });
    return this.http
      .post(
        `${environment.BASE_URL}/bot/history`,
        {
          user_id: this.userId,
          bot_id: this.botId,
        },
        { headers }
      )
      .pipe(
        switchMap((res: any) => {
          return of(res);
        }),
        catchError((error: any) => {
          console.error('Error fetching chat history: ', error);
          return of(null);
        })
      );
  }

  mapChatHistory(chats: any) {
    this.chatLog.push({
      type: 'ai',
      message:
        this.greetingMsg ||
        this.processMessage(
          "Greetings! I'm your COP28 virtual assistant. Have questions about the event? I'm here to help."
        ),
      time: formatNow(this.timezone),
    });
    if (chats && chats.length) {
      chats.forEach((chat: any) => {
        if (chat.User) {
          this.chatLog.push({
            type: 'user',
            message: this.processMessage(chat.User),
            time: formatTimeStamps(this.timezone, chat.Timestamp),
          });
        }
        if (chat.AI) {
          this.chatLog.push({
            type: 'ai',
            message: this.processMessage(chat.AI),
            time: formatTimeStamps(this.timezone, chat.Timestamp),
          });
          this.showFeedBackIconsIndex = this.chatLog.length - 1;
        }
      });
      this.showStartAgain = true;
    }
    this.scrollToBottom();
    this.cdr.markForCheck();
  }

  processMessage(message: string): SafeHtml {
    if (!message) {
      return '';
    }

    const markdownLinkRegex = /\[([^[]+)]\(([^)]+)\)/g;
    const urlRegex = /(?<!href=")\bhttps?:\/\/\S+(?<![.,])/gi;

    if (markdownLinkRegex.test(message)) {
      const html = message.replace(
        markdownLinkRegex,
        '<a href="$2" target="_blank">$1</a>'
      );
      console.log('Sanitized message: ', message);
      return this.sanitizeHtml(html);
    }

    if (urlRegex.test(message)) {
      const html = message.replace(
        urlRegex,
        '<a href="$&" target="_blank">$&</a>'
      );
      console.log('Sanitized message: ', message);
      return this.sanitizeHtml(html);
    }

    this.cdr.markForCheck();
    const sanitizedMessage = this.sanitizeHtml(message);
    return sanitizedMessage;
  }

  ngAfterViewChecked() {
    const feedbackLinks =
      this.elementRef.nativeElement.querySelectorAll('.feedback-link');
    feedbackLinks.forEach((link: any) => {
      link.addEventListener(
        'click',
        this.onFeedbackClick.bind(this, 'zendesk')
      );
    });
    // if (this.elementRef.nativeElement.querySelector('.feedback-link')) {
    //   this.elementRef.nativeElement
    //     .querySelector('.feedback-link')
    //     .addEventListener('click', this.onFeedbackClick.bind(this, 'zendesk'));
    // }
  }

  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  onFeedbackClick(value: string) {
    this.feedbackEvent.emit(value);
  }

  onClose() {
    this.renderer.removeClass(document.body, this.bodyOverflowClass);
    this.onCloseEvent.emit();
  }

  sendMessageWithTile(prompt: string) {
    this.input = prompt;
    this.fetchData();
    this.scrollToBottom();
  }

  fetchData() {
    if (!this.input.trim() || !this.userId || this.loading) {
      return;
    }

    this.chatLog.push({
      type: 'user',
      message: this.processMessage(this.input),
      time: formatNow(this.timezone),
    });

    this.cdr.markForCheck();

    this.aiResponse = '';
    this.loading = true;

    const url =
      `${environment.BASE_URL}/bot/ask-kb` +
      (this.useOpenAi === false ? '/llm-hf' : '');
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': this.apiKey,
      },
      body: JSON.stringify({
        query: this.input,
        user_id: this.userId,
        bot_id: this.botId,
      }),
    })
      // Retrieve its body as ReadableStream
      .then((response) => {
        return this.readAllChunks(response.body);
      })
      .catch((err) => {
        console.error('Err: ', err);
        this.loading = false;
      });

    this.input = '';
  }

  fetchSmallTalk() {
    this.loading = true;
    this.cdr.markForCheck();

    const url = `${environment.BASE_URL}/bot/small-talk/${this.botId}/${this.userId}`;

    this.http
      .get<{ smallTalk: string }>(url, {
        headers: {
          'x-api-key': this.apiKey,
        },
      })
      .pipe(
        catchError((error) => {
          console.error('Error while fetching small talk:', error);

          return of(null);
        })
      )
      .subscribe((response) => {
        this.loading = false;
        this.cdr.markForCheck();

        if (!response) {
          return;
        }

        if (response && response.smallTalk) {
          this.chatLog.push({
            type: 'ai',
            message: this.processMessage(response.smallTalk),
            time: formatNow(this.timezone),
            isFeedbackMsg: true,
          });
          this.showFeedBackIconsIndex = this.chatLog.length - 2;
          // this.scrollToBottom();
          this.cdr.markForCheck();
        }
      });
  }

  readAllChunks = (stream: any) => {
    const reader = stream.getReader();

    reader.closed.catch((err: any) => {
      if (err) {
        console.error('Error reading stream: ', err);
      }

      this.loading = false;
      this.scrollToBottom();
      return;
    });

    return new ReadableStream({
      start: (controller) => {
        return this.pump(controller, reader);
      },
    });
  };

  pump(controller: any, reader: any) {
    reader.read().then(({ done, value }: any) => {
      const lastItem = this.chatLog[this.chatLog.length - 1];

      if (done) {
        lastItem.message = this.processMessage(lastItem.message);
        this.chatLog.pop();
        this.chatLog.push(lastItem);

        controller.close();
        this.loading = false;
        // this.scrollToBottom();
        this.cdr.markForCheck();
        this.showStartAgain = true;

        setTimeout(() => {
          this.fetchSmallTalk();
        },1000)
        return;
      }

      const decodedChunk = this.decoder.decode(value, { stream: true });
      this.aiResponse += decodedChunk;

      if (lastItem.type === 'ai') {
        lastItem.message = this.aiResponse;
        this.cdr.markForCheck();
      } else {
        this.loading = false;

        this.chatLog.push({
          type: 'ai',
          message: this.aiResponse,
          time: formatNow(this.timezone),
        });
        this.showFeedBackIconsIndex = this.chatLog.length - 1;
        this.cdr.markForCheck();
      }

      this.scrollToBottom();

      // Enqueue the next data chunk into our target stream
      controller.enqueue(value);
      this.pump(controller, reader);
    });
  }

  submitFeedback(flag: boolean) {
    this.feedbackDone = true;
    this.showFeedBackIconsIndex = null;
    const url = `${environment.BASE_URL}/bot/feedback`;
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': this.apiKey,
      },
      body: JSON.stringify({ flag, user_id: this.userId, bot_id: this.botId }),
    }).then(() => {
      if (flag) {
        this.chatLog.push({
          type: 'ai',
          message:
            this.thumbsUpMessage ||
            `Great. May I assist you with anything else?`,
          time: formatNow(this.timezone),
          isFeedbackMsg: true,
        });

        this.scrollToBottom();
        this.cdr.markForCheck();
        this.feedbackDone = false;
        this.showFeedBackIconsIndex = null;
        return;
      }

      this.chatLog.push({
        type: 'ai',
        message: this.processMessage(
          this.thumbsDownMessages[this.thumbsDownMsgIndex]
        ),
        time: formatNow(this.timezone),
        isFeedbackMsg: true,
      });
      this.thumbsDownMsgIndex =
        (this.thumbsDownMsgIndex + 1) % this.thumbsDownMessages.length;
      this.scrollToBottom();
      this.cdr.markForCheck();
      this.feedbackDone = false;
      this.showFeedBackIconsIndex = null;
    });
  }

  handleSubmit() {
    this.fetchData();
    this.scrollToBottom();
  }

  handleUpClick(idx: any) {
    if (this.feedbackDone) {
      return;
    }

    this.submitFeedback(true);
    this.chatLog[idx].liked = !this.chatLog[idx].liked;

    if (this.chatLog[idx].unliked) {
      this.chatLog[idx].unliked = !this.chatLog[idx].unliked;
    }

    this.cdr.markForCheck();
  }

  handleDownClick(idx: any) {
    if (this.feedbackDone) return;
    this.submitFeedback(false);
    this.chatLog[idx].unliked = !this.chatLog[idx].unliked;
    if (this.chatLog[idx].liked)
      this.chatLog[idx].liked = !this.chatLog[idx].liked;
  }

  scrollToBottom() {
    let counter = 0;
    const interval = setInterval(() => {
      this.chatMain.nativeElement.scrollTop =
        this.chatMain.nativeElement.scrollHeight;
      if (counter++ > 5) clearInterval(interval);
    }, 5);
  }

  ngAfterViewInit() {
    // Check if the drawer is initially open and apply overflow hidden to body if so
    if (this.drawer.opened) {
      this.setBodyOverflow();
    }

    // Listen to changes in the drawer being opened or closed
    this.drawer.openedChange.subscribe((opened: boolean) => {
      if (opened) {
        this.setBodyOverflow();
      } else {
        this.removeBodyOverflow();
      }
    });

    this.drawer.openedChange.subscribe((opened) => {
      if (opened) {
        setTimeout(() => {
          if (this.myTextarea && this.myTextarea.nativeElement) {
            this.myTextarea.nativeElement.focus(); // Focus on the textarea
          }
        });
      }
    });
  }

  private setBodyOverflow() {
    this.renderer.addClass(document.body, this.bodyOverflowClass);
  }

  private removeBodyOverflow() {
    this.renderer.removeClass(document.body, this.bodyOverflowClass);
  }
}
